<script setup>
import { ref, onMounted, computed } from 'vue'
import moment from 'moment'
import numeral from 'numeral'
import { Heading } from '@/components/ui/typography'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { RefreshCw, Activity, Clock, AlertTriangle, CheckCircle } from 'lucide-vue-next'
import { axios } from '@/utils/axios'
import { useMeta } from 'vue-meta'
import { Line } from 'vue-chartjs'
import {
    Chart,
    LineElement,
    LinearScale,
    PointElement,
    CategoryScale,
    TimeScale,
    Tooltip,
    Legend
} from 'chart.js'
import 'chartjs-adapter-moment'
import { tailwindConfig, hexToRGB } from '@/utils/Utils'

Chart.register(LineElement, LinearScale, PointElement, CategoryScale, TimeScale, Tooltip, Legend)

useMeta({
    title: 'System Status - Admin',
})

const isLoading = ref(false)
const currentStatus = ref(null)
const statusHistory = ref([])
const error = ref(null)
const activeTab = ref('current')
const selectedHours = ref(24)

const hourOptions = [
    { value: 1, label: '1 Hour' },
    { value: 6, label: '6 Hours' },
    { value: 24, label: '24 Hours' },
    { value: 168, label: '7 Days' },
    { value: 720, label: '30 Days' },
]

const fetchCurrentStatus = async () => {
    try {
        isLoading.value = true
        error.value = null

        const response = await axios.get('/status/etl?latest=true')

        if (response.data.success) {
            currentStatus.value = response.data.data
        } else {
            error.value = 'Failed to fetch current status'
        }
    } catch (err) {
        error.value = err.response?.data?.message || 'Failed to fetch current status'
        console.error('Error fetching current status:', err)
    } finally {
        isLoading.value = false
    }
}

const fetchStatusHistory = async () => {
    try {
        isLoading.value = true
        error.value = null

        const response = await axios.get(`/status/etl/history?hours=${selectedHours.value}`)

        if (response.data.success) {
            statusHistory.value = response.data.data.history || []
        } else {
            error.value = 'Failed to fetch status history'
        }
    } catch (err) {
        error.value = err.response?.data?.message || 'Failed to fetch status history'
        console.error('Error fetching status history:', err)
    } finally {
        isLoading.value = false
    }
}

const refresh = () => {
    if (activeTab.value === 'current') {
        fetchCurrentStatus()
    } else {
        fetchStatusHistory()
    }
}

const getStatusBadgeVariant = (status) => {
    return status === 'healthy' ? 'default' : 'destructive'
}

const formatDuration = (ms) => {
    if (!ms) return 'N/A'
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`
}

const formatRelativeTime = (timestamp) => {
    return moment(timestamp).fromNow()
}

const platformNames = {
    youtube: 'YouTube',
    tiktok: 'TikTok',
    instagram: 'Instagram',
    twitter: 'X',
}

// Main chart configuration - Total overdue count
const chartData = computed(() => {
    if (!statusHistory.value || statusHistory.value.length === 0) {
        return { labels: [], datasets: [] }
    }

    const datasets = [
        {
            label: 'Total Overdue Count',
            data: statusHistory.value.map(status => ({
                x: new Date(status.ts),
                y: status.overall?.totalOverdueCount || 0
            })),
            backgroundColor: tailwindConfig().theme.colors.red[400],
            borderColor: tailwindConfig().theme.colors.red[500],
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: tailwindConfig().theme.colors.red[500],
            fill: false,
        }
    ]

    return {
        datasets
    }
})

const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        intersect: false,
        mode: 'index',
    },
    plugins: {
        legend: {
            position: 'bottom',
            labels: {
                usePointStyle: true,
            },
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                title: (context) => {
                    if (context.length > 0) {
                        const timestamp = context[0].parsed.x
                        return moment(timestamp).format('MMM D YYYY, h:mm A')
                    }
                    return ''
                },
                label: (context) => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y
                    return `${label}: ${numeral(value).format('0,0')}`
                },
            },
        },
    },
    scales: {
        x: {
            type: 'time',
            time: {
                unit: (() => {
                    if (selectedHours.value <= 6) return 'hour'
                    if (selectedHours.value <= 24) return 'hour'
                    if (selectedHours.value <= 168) return 'day'
                    return 'day'
                })(),
                stepSize: (() => {
                    if (selectedHours.value <= 6) return 1
                    if (selectedHours.value <= 24) return 1
                    if (selectedHours.value <= 168) return 1
                    return 2 // Show every 2 days for 30-day view
                })(),
                tooltipFormat: 'MMM D YYYY, h:mm A',
                displayFormats: {
                    hour: selectedHours.value <= 24 ? 'h:mm A' : 'MMM D h:mm A',
                    day: 'MMM D',
                },
            },
            grid: {
                display: true,
                drawOnChartArea: true,
                color: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[200])}, 0.3)`,
            },
            ticks: {
                maxRotation: 45,
                source: 'data',
                autoSkip: false,
            },
        },
        y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
                display: true,
                text: 'Overdue Count'
            },
            grid: {
                color: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[200])}, 0.3)`,
            },
            ticks: {
                callback: (value) => numeral(value).format('0.0a'),
            },
        }
    },
}))

// Platform chart configuration
const platformChartData = computed(() => {
    if (!statusHistory.value || statusHistory.value.length === 0) {
        return { labels: [], datasets: [] }
    }

    const platformColors = {
        youtube: { bg: tailwindConfig().theme.colors.red[400], border: tailwindConfig().theme.colors.red[500] },
        tiktok: { bg: tailwindConfig().theme.colors.pink[400], border: tailwindConfig().theme.colors.pink[500] },
        instagram: { bg: tailwindConfig().theme.colors.purple[400], border: tailwindConfig().theme.colors.purple[500] },
        twitter: { bg: tailwindConfig().theme.colors.x, border: tailwindConfig().theme.colors.slate[800] },
    }

    const datasets = Object.keys(platformNames).map(platform => ({
        label: platformNames[platform],
        data: statusHistory.value.map(status => ({
            x: new Date(status.ts),
            y: status.platforms?.[platform]?.overdueCount || 0
        })),
        backgroundColor: platformColors[platform].bg,
        borderColor: platformColors[platform].border,
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: platformColors[platform].border,
        fill: false,
    }))

    return {
        datasets
    }
})

const platformChartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        intersect: false,
        mode: 'index',
    },
    plugins: {
        legend: {
            position: 'bottom',
            labels: {
                usePointStyle: true,
            },
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                title: (context) => {
                    if (context.length > 0) {
                        const timestamp = context[0].parsed.x
                        return moment(timestamp).format('MMM D YYYY, h:mm A')
                    }
                    return ''
                },
                label: (context) => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y
                    return `${label}: ${numeral(value).format('0,0')}`
                },
            },
        },
    },
    scales: {
        x: {
            type: 'time',
            time: {
                unit: (() => {
                    if (selectedHours.value <= 6) return 'hour'
                    if (selectedHours.value <= 24) return 'hour'
                    if (selectedHours.value <= 168) return 'day'
                    return 'day'
                })(),
                stepSize: (() => {
                    if (selectedHours.value <= 6) return 1
                    if (selectedHours.value <= 24) return 1
                    if (selectedHours.value <= 168) return 1
                    return 2 // Show every 2 days for 30-day view
                })(),
                tooltipFormat: 'MMM D YYYY, h:mm A',
                displayFormats: {
                    hour: selectedHours.value <= 24 ? 'h:mm A' : 'MMM D h:mm A',
                    day: 'MMM D',
                },
            },
            grid: {
                display: true,
                drawOnChartArea: true,
                color: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[200])}, 0.3)`,
            },
            ticks: {
                maxRotation: 45,
                source: 'data',
                autoSkip: false,
            },
        },
        y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
                display: true,
                text: 'Overdue Count by Platform'
            },
            grid: {
                color: `rgba(${hexToRGB(tailwindConfig().theme.colors.slate[200])}, 0.3)`,
            },
            ticks: {
                callback: (value) => numeral(value).format('0.0a'),
            },
        }
    },
}))

onMounted(() => {
    fetchCurrentStatus()
})
</script>

<template>
    <div class="space-y-6">
        <header class="flex flex-col md:flex-row md:items-start md:justify-between">
            <div>
                <Heading :level="1" title="System Status" subtitle="Monitor ETL pipeline health and performance metrics" />
            </div>
            <Button class="mt-2 md:mt-0" :disabled="isLoading" @click="refresh">
                <RefreshCw :class="{ 'animate-spin': isLoading }" class="mr-2 h-4 w-4" />
                Refresh
            </Button>
        </header>

        <div v-if="error" class="rounded-lg border border-destructive/20 bg-destructive/10 p-4">
            <div class="flex items-center">
                <AlertTriangle class="mr-2 h-5 w-5 text-destructive" />
                <span class="font-medium text-destructive">{{ error }}</span>
            </div>
        </div>

        <Tabs v-model="activeTab">
            <TabsList class="grid inline-flex grid-cols-2 bg-slate-200 rounded">
                <TabsTrigger
                    value="current"
                    @click="
                        activeTab = 'current';
                        fetchCurrentStatus();
                    "
                >
                    Current Status
                </TabsTrigger>
                <TabsTrigger
                    value="history"
                    @click="
                        activeTab = 'history';
                        fetchStatusHistory();
                    "
                >
                    Historical Data
                </TabsTrigger>
            </TabsList>

            <!-- Current Status Tab -->
            <TabsContent value="current" class="space-y-6">
                    <div v-if="isLoading" class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <Card v-for="i in 6" :key="i">
                            <CardHeader class="space-y-2">
                                <Skeleton class="h-4 w-24" />
                                <Skeleton class="h-8 w-16" />
                            </CardHeader>
                        </Card>
                    </div>

                    <div v-else-if="currentStatus" class="space-y-6">
                        <!-- Overall Status Cards -->
                        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <Card>
                                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle class="text-sm font-medium">Overall Status</CardTitle>
                                    <Activity class="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <Badge :variant="getStatusBadgeVariant(currentStatus.overall?.status)">
                                        {{ currentStatus.overall?.status || 'unknown' }}
                                    </Badge>
                                    <p class="mt-2 text-xs text-muted-foreground">
                                        {{ formatRelativeTime(currentStatus.ts) }}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle class="text-sm font-medium">Total Overdue</CardTitle>
                                    <AlertTriangle class="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div class="text-2xl font-bold">
                                        {{ numeral(currentStatus.overall?.totalOverdueCount || 0).format('0,0') }}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle class="text-sm font-medium">Check Duration</CardTitle>
                                    <Clock class="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div class="text-2xl font-bold">
                                        {{ formatDuration(currentStatus.metadata?.totalDurationMs) }}
                                    </div>
                                    <p class="text-xs text-muted-foreground">
                                        Calc: {{ formatDuration(currentStatus.metadata?.calculationDurationMs) }}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle class="text-sm font-medium">Data Age</CardTitle>
                                    <Clock class="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div class="text-2xl font-bold">{{ currentStatus.age?.minutes || 0 }}m</div>
                                    <Badge v-if="currentStatus.age?.isStale" variant="destructive" class="text-xs">
                                        Stale
                                    </Badge>
                                    <Badge v-else variant="default" class="text-xs"> Fresh </Badge>
                                </CardContent>
                            </Card>
                        </div>

                        <!-- Platform Status Table -->
                        <Card>
                            <CardHeader>
                                <CardTitle>Platform Status</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table class="text-sm">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead class="px-4">Platform</TableHead>
                                            <TableHead class="px-4 text-right">Overdue Count</TableHead>
                                            <TableHead class="px-4 text-right">Query Duration</TableHead>
                                            <TableHead class="px-4 text-center">Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        <TableRow v-for="(data, platform) in currentStatus.platforms" :key="platform">
                                            <TableCell class="font-medium">
                                                {{ platformNames[platform] || platform }}
                                            </TableCell>
                                            <TableCell class="text-right">
                                                {{ numeral(data.overdueCount || 0).format('0,0') }}
                                            </TableCell>
                                            <TableCell class="text-right">
                                                {{ formatDuration(data.queryDurationMs) }}
                                            </TableCell>
                                            <TableCell class="text-center">
                                                <CheckCircle
                                                    v-if="data.overdueCount === 0"
                                                    class="inline h-4 w-4 text-green-500"
                                                />
                                                <AlertTriangle v-else class="inline h-4 w-4 text-red-500" />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                </div>
            </TabsContent>

            <!-- Historical Data Tab -->
            <TabsContent value="history" class="space-y-6">
                    <div class="flex items-center justify-between">
                        <div class="space-x-2">
                            <Button
                                v-for="option in hourOptions"
                                :key="option.value"
                                :variant="selectedHours === option.value ? 'default' : 'outline'"
                                size="sm"
                                @click="
                                    selectedHours = option.value;
                                    fetchStatusHistory();
                                "
                            >
                                {{ option.label }}
                            </Button>
                        </div>
                    </div>

                    <div v-if="isLoading" class="space-y-4">
                        <Card>
                            <CardHeader>
                                <Skeleton class="h-6 w-48" />
                            </CardHeader>
                            <CardContent class="space-y-2">
                                <Skeleton v-for="i in 10" :key="i" class="h-4 w-full" />
                            </CardContent>
                        </Card>
                    </div>

                    <div v-else-if="statusHistory.length > 0" class="space-y-6">
                        <!-- Total Overdue Chart -->
                        <Card>
                            <CardHeader>
                                <CardTitle>Total Overdue Count ({{ selectedHours }}h)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div class="h-[400px] w-full">
                                    <Line :data="chartData" :options="chartOptions" />
                                </div>
                            </CardContent>
                        </Card>

                        <!-- Platform Overdue Chart -->
                        <Card>
                            <CardHeader>
                                <CardTitle>Platform Overdue Counts ({{ selectedHours }}h)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div class="h-[400px] w-full">
                                    <Line :data="platformChartData" :options="platformChartOptions" />
                                </div>
                            </CardContent>
                        </Card>

                    </div>

                    <div v-else-if="!isLoading" class="py-8 text-center">
                        <p class="text-muted-foreground">No historical data available for the selected period.</p>
                    </div>
            </TabsContent>
        </Tabs>
    </div>
</template>
